import type { ActivityAction } from '@crew/shared';

/** תיאור בעברית של פעולות ביומן הפעילות. */
export const ACTIVITY_LABELS: Record<ActivityAction, string> = {
  'job.create': 'יצר/ה את העבודה',
  'job.update': 'עדכן/ה את פרטי העבודה',
  'job.delete': 'מחק/ה את העבודה',
  'job.registration.close': 'סגר/ה את ההרשמה',
  'job.registration.open': 'פתח/ה את ההרשמה',
  'job.archive': 'העביר/ה לארכיון',
  'job.unarchive': 'החזיר/ה לפעילות',
  'job.access.rotate': 'החליף/ה את פרטי הכניסה',
  'registration.add': 'הוסיף/ה נרשם',
  'registration.updateCount': 'שינה/תה כמות עובדים',
  'registration.remove': 'הסיר/ה נרשם',
  'registration.token.reissue': 'הנפיק/ה קישור אישי חדש',
  'registration.token.revoke': 'ביטל/ה קישור אישי',
  'contractor.update': 'עדכן/ה פרטי נרשם',
  'request.approve': 'אישר/ה בקשת שינוי',
  'request.reject': 'דחה/תה בקשת שינוי',
  'request.create': 'ביקש/ה שינוי',
  'request.cancel': 'ביטל/ה את הבקשה',
  'registration.self': 'נרשם/ה לעבודה',
  'announcement.create': 'פרסם/ה עדכון לנרשמים',
  'announcement.update': 'ערך/ה עדכון לנרשמים',
  'announcement.delete': 'מחק/ה עדכון לנרשמים',
  'admin.add': 'הוסיף/ה מנהל',
  'admin.disable': 'השבית/ה מנהל',
  'admin.enable': 'הפעיל/ה מנהל מחדש',
  'admin.remove': 'הסיר/ה מנהל',
  'admin.rename': 'שינה/תה שם של מנהל',
  'settings.update': 'עדכן/ה הגדרות מערכת',
  'admin.login': 'התחבר/ה',
};

export const FIELD_LABELS: Record<string, string> = {
  name: 'שם',
  date: 'תאריך',
  reportTime: 'שעת התייצבות',
  location: 'מיקום',
  notes: 'הערות',
  registrationClosesAt: 'סגירת הרשמה',
  registration: 'הרשמה',
  status: 'סטטוס',
  rotated: 'הוחלף',
  reason: 'סיבה',
  archiveHoldUntil: 'מוגן מארכוב עד',
  fullName: 'שם',
  workerCount: 'עובדים',
  registrantType: 'סוג',
  request: 'בקשה',
  text: 'תוכן',
  phone: 'טלפון',
  email: 'אימייל',
  active: 'פעיל',
  autoArchiveDaysAfter: 'ימים עד ארכוב',
  maxWorkersPerContractor: 'מקסימום עובדים לקבלן',
  maxAdmins: 'מספר מנהלים מקסימלי',
  whatsappSignature: 'חתימה בוואטסאפ',
  registrationNotice: 'הודעה במסך ההרשמה',
  personalPageNotice: 'הודעה בדף האישי',
  publicPaused: 'עצירת הרשמות',
  publicPausedMessage: 'הודעה בזמן עצירה',
};

const VALUE_LABELS: Record<string, string> = {
  open: 'פתוחה',
  closed: 'סגורה',
  active: 'פעילה',
  archived: 'בארכיון',
  auto: 'אוטומטית',
  deadline: 'מועד הסגירה הגיע',
  manual: 'ידנית',
  contractor: 'קבלן',
  worker: 'עובד',
};

export function formatActivityValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') {
    if (VALUE_LABELS[value]) return VALUE_LABELS[value]!;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) || field === 'registrationClosesAt' || field === 'archiveHoldUntil') {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', dateStyle: 'short', timeStyle: 'short' });
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value.slice(8)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
    return value.length > 80 ? `${value.slice(0, 80)}…` : value;
  }
  if (value instanceof Date) return value.toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', dateStyle: 'short', timeStyle: 'short' });
  if (typeof value === 'object' && value && 'toDate' in value) {
    return formatActivityValue(field, (value as { toDate(): Date }).toDate());
  }
  return String(value);
}

/** "לפני 5 דקות" / "היום 14:20" / "12.10 09:15" */
export function formatWhen(d: Date, now = new Date()): string {
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return 'עכשיו';
  if (diff < 3600) return `לפני ${Math.floor(diff / 60)} דק׳`;
  const tz = { timeZone: 'Asia/Jerusalem' } as const;
  const day = d.toLocaleDateString('en-CA', tz);
  const time = d.toLocaleTimeString('he-IL', { ...tz, hour: '2-digit', minute: '2-digit' });
  if (day === now.toLocaleDateString('en-CA', tz)) return `היום ${time}`;
  return `${day.slice(8)}.${day.slice(5, 7)} ${time}`;
}

/** שורות פירוט של פעולה ביומן — משמש גם במסך וגם בדוח ה-PDF. */
export function activityDetails(entry: { action: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null }): string[] {
  if (entry.action === 'job.create' || entry.action === 'job.delete') {
    const src = entry.after ?? entry.before ?? {};
    return ['name', 'date', 'location'].filter((f) => f in src).map((f) => `${FIELD_LABELS[f]}: ${formatActivityValue(f, src[f])}`);
  }
  const fields = Object.keys({ ...(entry.before ?? {}), ...(entry.after ?? {}) }).filter((f) => f in FIELD_LABELS);
  return fields.map((f) => {
    const hasBefore = entry.before && f in entry.before;
    const hasAfter = entry.after && f in entry.after;
    if (hasBefore && hasAfter) return `${FIELD_LABELS[f]}: ${formatActivityValue(f, entry.before![f])} ← ${formatActivityValue(f, entry.after![f])}`;
    return `${FIELD_LABELS[f]}: ${formatActivityValue(f, hasAfter ? entry.after![f] : entry.before![f])}`;
  });
}

export function actorKind(actorUid: string): 'admin' | 'system' | 'contractor' {
  if (actorUid === 'system') return 'system';
  if (actorUid.startsWith('contractor:')) return 'contractor';
  return 'admin';
}
